﻿import DefaultApp from './defaultApp';
import { useVuelidate } from '@vuelidate/core';
import { required, requiredIf, email, sameAs, between, helpers } from '@vuelidate/validators';
import datePicker from 'vanillajs-datepicker/Datepicker';
import nl from 'vanillajs-datepicker/locales/nl';
Object.assign(datePicker.locales, nl);

const isValid = 'Success';
const dutchPostal = helpers.regex(/^[1-9][0-9]{3} ?(?!sa|sd|ss|SA|SD|SS)[A-Za-z]{2}$/);
const today = new Date();
const yesterday = new Date(today.setDate(today.getDate() - 1));
const hundredYearsAgo = new Date();
hundredYearsAgo.setFullYear(hundredYearsAgo.getFullYear() - 100);
let maxdate = yesterday.toLocaleDateString('nl-NL', { year: 'numeric', month: 'short', day: '2-digit' });
let minbirthdate = hundredYearsAgo.toLocaleDateString('nl-NL', { year: 'numeric', month: 'short', day: '2-digit' });

const birthOptions = {
    autohide: true,
    format: 'dd-M-yyyy',
    maxDate: maxdate,
    minDate: minbirthdate,
    language: 'nl',
    nextArrow: '>',
    prevArrow: '<',
    startView: 2,
    orientation: 'top'
}
function validateIf(condition, rule) {
    return condition ? rule : {};
}

const registerApp = {
    ...DefaultApp,
    setup() {
        return { v$: useVuelidate() }
    },
    data() {
        return {
            ...DefaultApp.data(),
            api: '',
            returnUrl: '',
            english: false,
            formData: new FormData(),
            prefilled: false,
            data: {
                typeAlias: isValid,
            },
            datepickers: [],
            isBusiness: false,
            canRegister: true,
            step: 1,
            steps: {},
            step1: {
                registrationCode: '',
                isRegistrationNumberCorrect: true,
                isAccountAlreadyCreated: false
            },
            step2: {
                postalCode: '',
                isForeignAddress: false,
                isPostalcodeCorrect: true,
                isBirthdateKnown: false,
                isPhonenumberKnown: false
            },
            step3: {
                business: {
                    chamberOfCommerceNumber: '',
                    isChamberOfCommerceNumberCorrect: true
                },
                naturalEntity: {
                    birthdate: '',
                    phonenumber: '',
                    isBirthdateCorrect: true,
                    isPhonenumberCorrect: true
                }
            },
            step4: {
                email: '',
                confirmEmail: '',
                isEmailadresNew: true
            },
            kvkCount: 0,
            birthdayCount: 0,
            telCount: 0
        }
    },
    validations() {
        return {
            step1: {
                registrationCode: { required }
            },
            step2: {
                postalCode: {
                    required,
                    ...validateIf(!this.step2.isForeignAddress, { dutchPostal })
                }
            },
            step3: {
                business: {
                    chamberOfCommerceNumber: {
                        required: requiredIf(function () {
                            return this.isBusiness
                        })
                    }
                },
                naturalEntity: {
                    birthdate: {
                        required: requiredIf(function () {
                            return !this.isBusiness && this.step2.isBirthdateKnown
                        }),
                        ...validateIf(!this.isBusiness && this.step2.isBirthdateKnown, {
                            between(date) {
                                let dateObj = this.getDateFromString(date);
                                return dateObj > hundredYearsAgo && dateObj < today
                            }
                        })
                    },
                    phonenumber: {
                        required: requiredIf(function () {
                            return !this.isBusiness && !this.step2.isBirthdateKnown && this.step2.isPhonenumberKnown
                        })
                    }
                }
            },
            step4: {
                email: { required, email },
                confirmEmail: {
                    required,
                    email,
                    sameAs: sameAs(this.step4.email)
                }
            }
        }
    },
    watch: {
        "step1.registrationCode"(newVal, oldVal) {
            if (newVal !== oldVal) {
                this.step1.isRegistrationNumberCorrect = true;
                this.step1.isAccountAlreadyCreated = false;
                this.kvkCount = 0;
                this.birthdayCount = 0;
                this.telCount = 0;
            }
        },
        "step3.business.chamberOfCommerceNumber"(newVal, oldVal) {
            if (newVal !== oldVal) {
                this.step3.business.isChamberOfCommerceNumberCorrect = true;
            }
        },
        "step3.naturalEntity.birthdate"(newVal, oldVal) {
            if (newVal !== oldVal) {
                this.step3.naturalEntity.isBirthdateCorrect = true;
            }
        },
        "step3.naturalEntity.phonenumber"(newVal, oldVal) {
            if (newVal !== oldVal) {
                this.step3.naturalEntity.isPhonenumberCorrect = true;
            }
        },
        "step4.email"(newVal, oldVal) {
            if (newVal !== oldVal) {
                this.step4.isEmailadresNew = true;
            }
        }
    },
    methods: {
        ...DefaultApp.methods,
        async submitCode() {
            const valid = await this.v$.step1.$validate();
            if (valid) {
                this.formData.set('RegistrationCode', this.step1.registrationCode);
                const response = await this.fetchData(this.formData, this.api, 'post');
                if (response.status === 200) {
                    this.data = response.data;
                    this.step1.isRegistrationNumberCorrect = this.data.registerFormState.isRegistrationNumberCorrect;
                    this.step1.isAccountAlreadyCreated = this.data.registerFormState.isAccountAlreadyCreated;
                    if (this.data.typeAlias === isValid && this.step1.isRegistrationNumberCorrect && !this.step1.isAccountAlreadyCreated) {
                        this.step = 2;
                    }
                }
            }
        },
        async submitPostal() {
            const valid = await this.v$.step2.$validate();
            if (valid) {
                this.formData.set('Postalcode', this.step2.postalCode);
                const response = await this.fetchData(this.formData, this.api, 'post');
                if (response.status === 200) {
                    this.data = response.data;
                    if (this.data.typeAlias === isValid) {
                        this.isBusiness = this.data.registerFormState.isBusiness;
                        this.step2.isPostalcodeCorrect = this.data.registerFormState.isPostalcodeCorrect;
                        this.step2.isBirthdateKnown = this.data.registerFormState.isBirthdateKnown;
                        this.step2.isPhonenumberKnown = this.data.registerFormState.isPhonenumberKnown;
                        if (this.isBusiness || this.step2.isBirthdateKnown || this.step2.isPhonenumberKnown) {
                            this.step = 3;
                            if (!this.isBusiness && this.step2.isBirthdateKnown) {
                                this.$nextTick(() => {
                                    this.initCalendars(this.datepickers);
                                });
                            }
                        } else {
                            this.step = 3;
                            this.canRegister = false;
                        }
                    }
                }
            }
        },
        async submitChamberOfCommerce() {
            const valid = await this.v$.step3.business.chamberOfCommerceNumber.$validate();
            if (valid) {
                this.formData.set('ChamberOfCommerceNumber', this.step3.business.chamberOfCommerceNumber);
                const response = await this.fetchData(this.formData, this.api, 'post');
                if (response.status === 200) {
                    this.data = response.data;
                    if (this.data.typeAlias === isValid) {
                        this.step3.business.isChamberOfCommerceNumberCorrect = this.data.registerFormState.isChamberOfCommerceNumberCorrect;
                        if (this.step3.business.isChamberOfCommerceNumberCorrect) {
                            this.step = 4;
                        } else {
                            this.kvkCount++;
                        }
                    }
                }
            }
        },
        async submitBirthday() {
            const valid = await this.v$.step3.naturalEntity.birthdate.$validate();
            if (valid) {
                this.formData.set('Birthdate', this.getDateFromString(this.step3.naturalEntity.birthdate).toISOString());
                const response = await this.fetchData(this.formData, this.api, 'post');
                if (response.status === 200) {
                    this.data = response.data;
                    if (this.data.typeAlias === isValid) {
                        this.step3.naturalEntity.isBirthdateCorrect = this.data.registerFormState.isBirthdateCorrect;
                        if (this.step3.naturalEntity.isBirthdateCorrect) {
                            this.step = 4;
                        } else {
                            this.birthdayCount++;
                        }
                    }
                }
            }
        },
        async submitPhonenumber() {
            const valid = await this.v$.step3.naturalEntity.phonenumber.$validate();
            if (valid) {
                this.formData.set('Phonenumber', this.step3.naturalEntity.phonenumber);
                const response = await this.fetchData(this.formData, this.api, 'post');
                if (response.status === 200) {
                    this.data = response.data;
                    if (this.data.typeAlias === isValid) {
                        this.step3.naturalEntity.isPhonenumberCorrect = this.data.registerFormState.isPhonenumberCorrect;
                        if (this.step3.naturalEntity.isPhonenumberCorrect) {
                            this.step = 4;
                        } else {
                            this.telCount++;
                        }
                    }
                }
            }
        },
        async submitEmail() {
            const valid = await this.v$.step4.$validate();
            if (valid) {
                this.formData.set('EmailAddress', this.step4.email);
                const response = await this.fetchData(this.formData, this.api, 'post');
                if (response.status === 200) {
                    this.data = response.data;
                    if (this.data.typeAlias === isValid) {
                        if (this.data.registerFormState.isEmailadresNew !== null) {
                            this.step4.isEmailadresNew = this.data.registerFormState.isEmailadresNew
                            if (this.step4.isEmailadresNew) {
                                this.step = 5;
                            }
                        } else {
                            this.canRegister = false;
                        }
                    }
                }
            }
        },
        setDateRef(el) {
            if (el) {
                if (!this.datepickers.includes(el)) this.datepickers.push(el);
            }
        },
        initCalendars(calendars) {
            calendars.forEach(calendar => {
                let format = calendar.dataset.showformat;
                birthOptions.format = format;
                new datePicker(calendar, birthOptions);
                calendar.addEventListener('changeDate', (e) => {
                    calendar.dispatchEvent(new Event('input', { bubbles: true }));
                });
            });
        },
        redirect() {
            window.location.href = this.returnUrl;
        },
        back(step) {
            this.v$.$reset();
            if (step === 1) {
                this.step2 = JSON.parse(JSON.stringify(this.steps.step2));
            } else if (step === 2) {
                this.step3 = JSON.parse(JSON.stringify(this.steps.step3));
            } else if (step === 3) {
                this.step4 = JSON.parse(JSON.stringify(this.steps.step4));
            }
            this.step = step;
        }
    },
    mounted() {
        if (this.$el.parentNode.dataset.apiurl) this.api = this.$el.parentNode.dataset.apiurl;
        if (this.$el.parentNode.dataset.returnurl) this.returnUrl = this.$el.parentNode.dataset.returnurl;
        if (this.$el.parentNode.dataset.registrationcode) this.step1.registrationCode = this.$el.parentNode.dataset.registrationcode;
        if (this.step1.registrationCode !== '') this.prefilled = true;
        this.steps.step1 = JSON.parse(JSON.stringify(this.step1));
        this.steps.step2 = JSON.parse(JSON.stringify(this.step2));
        this.steps.step3 = JSON.parse(JSON.stringify(this.step3));
        this.steps.step4 = JSON.parse(JSON.stringify(this.step4));
    }
}

export default registerApp;